import { useMemo } from 'react'
import { MoreHorizontal, Pencil, Archive, ArchiveRestore } from 'lucide-react'
import type { Tour } from '@/types'
import type { TourStats } from '@/hooks/useOrgTourStats'
import { useNow } from '@/hooks/useNow'
import {
  computeTourState,
  formatTourDates,
  shortCountdown,
  tourAccent,
} from '@/lib/tourState'
import { formatInTimezone } from '@/lib/timezones'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface Props {
  tour: Tour
  stats: TourStats | undefined
  onOpen: (tour: Tour) => void
  onEdit: (tour: Tour) => void
  onArchive: (tour: Tour) => void
  onActivate: (tour: Tour) => void
}

const STATUS_LABEL: Record<string, string> = {
  upcoming: 'Upcoming',
  live: 'Live now',
  wrapping: 'Wrapping up',
  past: 'Finished',
}

export function TourCard({ tour, stats, onOpen, onEdit, onArchive, onActivate }: Props) {
  // Re-render every minute so the countdown stays fresh.
  useNow(60_000)

  const accent = tourAccent(tour.id)
  const state = useMemo(
    () => computeTourState(tour, stats?.activities ?? []),
    [tour, stats?.activities],
  )
  const crew = stats?.crew ?? []
  const visibleCrew = crew.slice(0, 5)
  const restCrew = Math.max(0, crew.length - visibleCrew.length)

  const isArchived = tour.estado === 'archived'
  const next = state.nextActivity
  const nextLabel = state.status === 'wrapping' ? 'Final show' : 'Up next'

  return (
    <div
      role="button"
      tabIndex={0}
      data-accent={accent}
      onClick={() => onOpen(tour)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(tour)
        }
      }}
      className="tf-tour-card group animate-in fade-in dur-base ease-out-soft"
    >
      <div className="tf-tour-strip" />

      <div className="flex flex-col gap-4 p-5">
        {/* Status row */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="tf-status-pill">
              {state.status === 'live' && <span className="live-dot" />}
              {STATUS_LABEL[state.status]}
            </span>
            <span className="font-mono text-[11px] text-muted-foreground">
              <strong className="text-foreground font-medium">Day {state.dayN}</strong>
              <span className="opacity-70"> / {state.dayTotal}</span>
            </span>
          </div>

          <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger
                className="p-1.5 -m-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Tour actions"
              >
                <MoreHorizontal className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => onEdit(tour)}>
                  <Pencil className="w-3.5 h-3.5 mr-2" />
                  Edit details
                </DropdownMenuItem>
                {isArchived ? (
                  <DropdownMenuItem onClick={() => onActivate(tour)}>
                    <ArchiveRestore className="w-3.5 h-3.5 mr-2" />
                    Reactivate
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => onArchive(tour)}>
                    <Archive className="w-3.5 h-3.5 mr-2" />
                    Archive
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Title */}
        <div>
          <h2 className="text-[22px] font-semibold leading-tight tracking-[-0.02em] text-foreground truncate">
            {tour.nombre}
          </h2>
          <div className="font-mono text-[11.5px] text-muted-foreground mt-1 tracking-wide">
            {formatTourDates(tour)}
          </div>
        </div>

        {/* Progress */}
        <div className="flex flex-col gap-2.5">
          <div className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
            <span>Tour progress</span>
            <span>
              <strong style={{ color: 'var(--accent)' }} className="font-medium">
                {state.progressPct}%
              </strong>
              <span className="text-subtle-foreground"> · day {state.dayN} of {state.dayTotal}</span>
            </span>
          </div>
          <div className="tf-progress-track">
            <div className="tf-progress-fill" style={{ ['--pct' as string]: `${state.progressPct}%` }} />
          </div>
        </div>

        {/* Date strip */}
        <div className="flex flex-col gap-2">
          <div className="tf-day-strip">
            {state.cells.map(c => (
              <div
                key={c.date}
                className={cn(
                  'tf-day-cell',
                  c.isPast && 'is-past',
                  c.isToday && 'is-today',
                  c.isTravel && 'is-travel',
                )}
                title={`${c.date}${c.isShow ? ' · show' : c.isTravel ? ' · travel' : ''}`}
              >
                <span className="tf-day-num">{c.num}</span>
                <span className="tf-day-dow">{c.dow}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 border-y border-border py-3.5">
          <Stat label="Shows" value={String(state.showDays)} />
          <Stat label="Travel" value={String(state.travelDays)} bordered />
          <Stat label="Crew" value={String(crew.length)} bordered />
        </div>

        {/* Up next — only when there's a future activity. */}
        {next && (
          <div className="flex flex-col gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-subtle-foreground">
              {nextLabel}
            </span>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-mono text-[12px] font-medium text-foreground">
                {formatInTimezone(next.start_time_utc, next.venue_timezone)}
              </span>
              <span className="text-[13px] font-medium text-foreground truncate">
                {next.venue_name || next.titulo}
              </span>
              <span
                className="font-mono text-[11px] ml-auto"
                style={{ color: 'var(--accent)' }}
              >
                {shortCountdown(next.start_time_utc)}
              </span>
            </div>
          </div>
        )}

        {/* Crew avatars */}
        {crew.length > 0 && (
          <div className="flex items-center gap-2.5 pt-1">
            <div className="tf-crew-stack">
              {visibleCrew.map(m => (
                <div key={m.user_id} className="tf-crew-av" title={m.profile?.nombre ?? ''}>
                  {m.profile?.avatar_url ? (
                    <img src={m.profile.avatar_url} alt="" />
                  ) : (
                    initialsOf(m.profile?.nombre)
                  )}
                </div>
              ))}
            </div>
            {restCrew > 0 && (
              <span className="font-mono text-[11px] text-muted-foreground">+ {restCrew}</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value, bordered }: { label: string; value: string; bordered?: boolean }) {
  return (
    <div className={cn('flex flex-col gap-1 px-3.5', bordered && 'border-l border-border')}>
      <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-subtle-foreground font-medium">
        {label}
      </span>
      <span className="text-[20px] font-semibold tracking-[-0.025em] text-foreground tabular-nums">
        {value}
      </span>
    </div>
  )
}

function initialsOf(name: string | null | undefined): string {
  if (!name) return '·'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Dashed empty-state card for the 0-active case.
export function PlanNewTourCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-accent="teal"
      className="tf-tour-card is-empty min-h-[360px] p-0 text-left"
    >
      <div className="tf-tour-strip" style={{ background: 'var(--border)', opacity: 0.4 }} />
      <div className="flex-1 p-5 flex flex-col gap-3.5 max-w-[260px] justify-center">
        <div className="w-9 h-9 rounded-[10px] border border-dashed border-border-strong grid place-items-center text-muted-foreground">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </div>
        <div>
          <div className="text-[16px] font-semibold tracking-[-0.015em] mb-1.5">
            Plan a new tour
          </div>
          <div className="text-[12.5px] text-muted-foreground leading-[1.5]">
            Drop in dates, invite crew, then start adding shows.
          </div>
        </div>
        <span
          className="font-mono text-[10.5px] uppercase tracking-[0.08em] font-medium"
          style={{ color: 'var(--accent)' }}
        >
          Start →
        </span>
      </div>
    </button>
  )
}
